import { SaveTempDraft } from '../../types/saveTemp';
import type { SaveData } from '../../types/save';
import { buildSaveTempPathAndName, mapCategoriaToPastaName } from '../../utils/fileNaming';
import { sendToTerminal } from '../../lib/terminalLogger';
import { resolveFilePath, type SaveTempPayload } from '../../services/pathResolverService';
import { requestConfirmation } from '../../lib/globalConfirm';

export type TempBuildContext = {
  draft: SaveTempDraft;
  totalObjectsWithQuantity: number;
};

export type TempBuildParams = TempBuildContext & {
  contraction: string;
  hasMultipleContractions: boolean;
  entrega?: string; // HOJE | AMANHÃ | NOTURNA | URGENTE
};

export async function buildTempFilePathAndName(params: TempBuildParams): Promise<{ 
  directoryInternal: string; 
  directoryExternal: string; 
  fileName: string; 
  fullPathInternal: string; 
  fullPathExternal: string 
}> {
  const { draft, totalObjectsWithQuantity, contraction, hasMultipleContractions, entrega } = params;
  
  try {
    sendToTerminal('SAVE_TEMP:BUILD_PATH_START', {
      categoriaId: draft.prod_cat_item_id,
      categoriaName: draft.prod_cat_item_name,
      baseName: draft.baseName,
      quality: draft.quality,
      entrega,
      totalObjectsWithQuantity
    });
  } catch {}
  
  // Validar categoriaId
  if (!draft.prod_cat_item_id || draft.prod_cat_item_id <= 0) {
    const error = `ID da categoria (prod_cat_item_id) é obrigatório para buscar diretório base. Valor recebido: ${draft.prod_cat_item_id}`;
    try {
      sendToTerminal('SAVE_TEMP:BUILD_PATH_ERROR', { error, stage: 'validation', categoriaId: draft.prod_cat_item_id });
    } catch {}
    throw new Error(error);
  }

  // Mapear categoria para nome de pasta (necessário para backend e fallback)
  const serviceType = mapCategoriaToPastaName(draft.prod_cat_item_id, draft.prod_cat_item_name);

  try {
    sendToTerminal('SAVE_TEMP:MAP_CATEGORIA_SUCCESS', {
      categoriaId: draft.prod_cat_item_id,
      serviceType,
    });
  } catch {}

  // ✅ Backend PathResolver com fallback local
  try {
    const payload: SaveTempPayload = {
      flow: 'save_temp',
      baseName: draft.baseName,
      serviceType,
      quality: draft.quality,
      totalObjectsWithQuantity,
      contraction: contraction || '0px',
      hasMultipleContractions: hasMultipleContractions || false,
      entrega: String(entrega || 'HOJE'),
      categoriaId: draft.prod_cat_item_id,
    };

    const resolved = await resolveFilePath(payload);

    sendToTerminal('✅ [SAVE_TEMP] Path resolvido via backend', {
      directory: resolved.smb.directory,
      fileName: resolved.smb.fileName,
      storageMethod: resolved.metadata.storageMethod,
    });

    return {
      directoryInternal: resolved.smb.directory,
      directoryExternal: resolved.smb.directory,
      fileName: resolved.smb.fileName,
      fullPathInternal: resolved.smb.fullPath,
      fullPathExternal: resolved.smb.fullPath,
    };
  } catch (backendError) {
    const errorMsg = backendError instanceof Error ? backendError.message : String(backendError);
    sendToTerminal('⚠️ [SAVE_TEMP] Backend indisponível após retries', { error: errorMsg });

    const useFallback = await requestConfirmation({
      title: 'Backend indisponível',
      message: `Não foi possível resolver o caminho via servidor.\n\nDeseja usar o cálculo local como alternativa?\n\n(Erro: ${errorMsg})`,
      confirmLabel: 'Usar cálculo local',
      cancelLabel: 'Cancelar',
    });

    if (!useFallback) {
      throw new Error('Salvamento cancelado — backend indisponível.');
    }

    sendToTerminal('🔄 [SAVE_TEMP] Usuário aceitou fallback local');

    // Fallback: lógica local original
    const result = await buildSaveTempPathAndName({
      baseName: draft.baseName,
      serviceType,
      quality: draft.quality,
      totalObjectsWithQuantity,
      contraction,
      hasMultipleContractions,
      entrega: String(entrega || 'HOJE'),
      categoriaId: draft.prod_cat_item_id,
    });

    try {
      sendToTerminal('SAVE_TEMP:BUILD_PATH_SUCCESS (fallback)', {
        directoryInternal: result.directoryInternal,
        fileName: result.fileName,
      });
    } catch {}

    return result;
  }
}

export function computeObjectsTotal(saveData: SaveData | null | undefined, fallback: number): number {
  if (saveData && Array.isArray(saveData.objetos) && saveData.objetos.length > 0) {
    const total = saveData.objetos.reduce((sum: number, obj: unknown) => {
      const raw = (obj as Record<string, unknown>)?.quantity ?? (obj as Record<string, unknown>)?.quantidade ?? 0;
      const parsed = Number(raw);
      return sum + (Number.isFinite(parsed) ? parsed : 0);
    }, 0);
    if (total > 0) return total;
  }
  return fallback > 0 ? fallback : 1;
}

export function makeChecklistTempPathBuilder(ctx: TempBuildContext) {
  return async (saveData: SaveData): Promise<{ filePath: string; fileName: string }> => {
    const contraction = saveData?.configuracoes?.contracao || '0px';
    const hasMultiple = !!(saveData?.configuracoes as Record<string, unknown>)?.contracoesVariaveis;
    const entregaSel = String(saveData?.configuracoes?.entrega || 'HOJE');
    const totalObjects = computeObjectsTotal(saveData, ctx.totalObjectsWithQuantity);
    try {
      sendToTerminal?.('SAVE_TEMP:TOTAL_OBJECTS', {
        stage: 'checklist_builder',
        total: totalObjects,
        fromSaveData: Array.isArray(saveData?.objetos) ? saveData.objetos.length : null,
        fallback: ctx.totalObjectsWithQuantity
      });
    } catch {
      /* Ignora erros de log */
    }
    const built = await buildTempFilePathAndName({
      draft: ctx.draft,
      totalObjectsWithQuantity: totalObjects,
      contraction,
      hasMultipleContractions: hasMultiple,
      entrega: entregaSel
    });
    // O caminho utilizado para salvar é o interno
    return { filePath: built.fullPathInternal, fileName: built.fileName };
  };
}


